import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';

export interface SearchOption {
  value: string;
  label: string;
  /** A quieter second part, e.g. "790 on hand". Searched as well. */
  hint?: string;
}

/**
 * A dropdown you can type into, for lists too long to scroll: boards, jobs.
 *
 * Unlike OptionInput (free text with suggestions), the result is always one of
 * the options -- a board or job that exists -- never whatever was typed.
 * Every word typed has to appear somewhere in the option, in any order, so
 * "250 61" finds "Grey Back · 250 GSM · 91.5 × 61 cm".
 *
 * Keyboard: arrows move, Enter picks, Escape closes. The list is a plain
 * listbox, so screen readers announce it as one.
 */
export default function SearchSelect({
  id, options, value, onChange, placeholder = 'Search…', emptyLabel, autoFocus,
}: {
  id?: string;
  options: SearchOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Offered first, e.g. "None — general stock". Picking it sets value to ''. */
  emptyLabel?: string;
  autoFocus?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  // Focused for you when a form opens, the field waits: the list opens when
  // you type, click or press the down arrow -- not unasked, over the form.
  const skipAutoFocusOpen = useRef(Boolean(autoFocus));

  const selected = options.find((o) => o.value === value);

  const shown = useMemo(() => {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    const matches = words.length === 0 ? options : options.filter((o) => {
      const text = `${o.label} ${o.hint ?? ''}`.toLowerCase();
      return words.every((w) => text.includes(w));
    });
    return emptyLabel && words.length === 0
      ? [{ value: '', label: emptyLabel }, ...matches]
      : matches;
  }, [options, query, emptyLabel]);

  useEffect(() => { setActive(0); }, [query, open]);

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  function pick(o: SearchOption) {
    onChange(o.value);
    setQuery('');
    setOpen(false);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActive((a) => Math.min(a + 1, shown.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && shown[active]) { e.preventDefault(); pick(shown[active]!); }
    } else if (e.key === 'Escape') {
      if (open) { e.preventDefault(); e.stopPropagation(); setOpen(false); setQuery(''); }
    }
  }

  const listId = id ? `${id}-list` : undefined;

  return (
    <div style={{ position: 'relative', flex: 1 }}>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
        autoFocus={autoFocus}
        // Closed, it shows what is chosen; open, it is the search box.
        value={open ? query : selected ? `${selected.label}${selected.hint ? ` · ${selected.hint}` : ''}` : ''}
        placeholder={open ? placeholder : (emptyLabel ?? placeholder)}
        onFocus={() => {
          if (skipAutoFocusOpen.current) { skipAutoFocusOpen.current = false; return; }
          setOpen(true);
        }}
        onClick={() => setOpen(true)}
        onBlur={() => { setOpen(false); setQuery(''); }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={onKeyDown}
        style={{ width: '100%' }}
      />
      {open && (
        <ul
          id={listId}
          ref={listRef}
          role="listbox"
          style={{
            position: 'absolute', zIndex: 40, left: 0, right: 0, top: 'calc(100% + 4px)',
            margin: 0, padding: 4, listStyle: 'none', maxHeight: 260, overflowY: 'auto',
            background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 8,
            boxShadow: '0 12px 28px rgba(0, 0, 0, .35)',
          }}
        >
          {shown.length === 0 && (
            <li className="dim" style={{ padding: '8px 10px', fontSize: 13 }}>Nothing matches “{query}”</li>
          )}
          {shown.map((o, i) => (
            <li
              key={o.value || '__empty'}
              role="option"
              aria-selected={o.value === value}
              // mousedown, not click: picking must happen before the input's
              // blur closes the list.
              onMouseDown={(e) => { e.preventDefault(); pick(o); }}
              onMouseEnter={() => setActive(i)}
              style={{
                padding: '7px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13.5,
                display: 'flex', gap: 8, justifyContent: 'space-between',
                background: i === active ? 'var(--row-hover)' : undefined,
                fontWeight: o.value === value ? 650 : undefined,
              }}
            >
              <span className={o.value === '' ? 'dim' : undefined}>{o.label}</span>
              {o.hint && <span className="dim" style={{ whiteSpace: 'nowrap' }}>{o.hint}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
